/**
 * MetricEntryGrid — Reusable spreadsheet-like data entry component for KPI metrics.
 *
 * Designed for two surfaces:
 * 1. Per-project inline entry (Mission tab within Performance)
 * 2. Future consolidated "my data obligations" task window
 *
 * Users scan rows, type values, tab between fields, and batch-save.
 */

import React, { useCallback, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./MetricEntryGrid.module.css";

// ── Types ───────────────────────────────────────────────────

/** Value type — controls how the input and display render for this metric. */
export type MetricValueType = "numeric" | "binary" | "percentage" | "milestone_set";

/** A single metric row definition passed in by the host component. */
export interface MetricRowDef {
  /** Unique key for this metric (e.g. subKpiKey) */
  key: string;
  /** Human-readable label */
  label: string;
  /** Unit of measurement (e.g. "trees", "kg CO₂", "%") */
  unit?: string;
  /** Most recent recorded value (for display context) */
  currentValue?: number | null;
  /** Target/goal value (for display context) */
  targetValue?: number | null;
  /** Direction hint */
  direction?: "increase_is_good" | "decrease_is_good" | "target_is_good";
  /** Value type — defaults to "numeric". Binary renders as Yes/No select. */
  valueType?: MetricValueType;
  /** Milestone total (for milestone_set type) */
  milestoneTotal?: number;
}

/** A single entry the user is composing (one row in the grid). */
export interface MetricEntryValue {
  key: string;
  value: string; // kept as string for controlled input
  note: string;
  dirty: boolean;
}

/** What the host component receives on save. */
export interface MetricEntrySavePayload {
  key: string;
  value: number;
  note: string;
  reportingDate: string; // YYYY-MM-DD
}

export interface MetricEntryGridProps {
  /** The metric definitions to show as rows */
  metrics: MetricRowDef[];
  /** Called when user clicks Save All — host handles API calls */
  onSave: (entries: MetricEntrySavePayload[]) => Promise<void>;
  /** Default reporting date (YYYY-MM-DD), defaults to today */
  defaultDate?: string;
  /** Whether the grid is read-only (user lacks permission) */
  readOnly?: boolean;
  /** Optional title override */
  title?: string;
  /** Hide the date selector (useful when host controls the date externally) */
  hideDateSelector?: boolean;
  /** External reporting date override */
  reportingDate?: string;
  /** Called when reporting date changes */
  onDateChange?: (date: string) => void;
}

// ── Helpers ─────────────────────────────────────────────────

function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatNum(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Format a value for display in Current/Target columns, respecting value type. */
function formatDisplay(
  v: number | null | undefined,
  valueType?: MetricValueType,
  milestoneTotal?: number
): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  switch (valueType) {
    case "binary":
      return v >= 1 ? "Yes" : "No";
    case "percentage":
      return `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case "milestone_set":
      return `${v} of ${milestoneTotal ?? "?"}`;
    default:
      return formatNum(v);
  }
}

// ── Component ───────────────────────────────────────────────

const MetricEntryGrid: React.FC<MetricEntryGridProps> = ({
  metrics,
  onSave,
  defaultDate,
  readOnly = false,
  title,
  hideDateSelector = false,
  reportingDate: externalDate,
  onDateChange,
}) => {
  const { t } = useAppTranslation("kpi");

  const [internalDate, setInternalDate] = useState<string>(
    defaultDate || todayISO()
  );
  const reportingDate = externalDate ?? internalDate;
  const handleDateChange = useCallback(
    (val: string) => {
      if (onDateChange) onDateChange(val);
      else setInternalDate(val);
    },
    [onDateChange]
  );

  // Build initial entry state from metrics
  const initialEntries = useMemo(
    () =>
      metrics.map(
        (m): MetricEntryValue => ({
          key: m.key,
          value: "",
          note: "",
          dirty: false,
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metrics.map((m) => m.key).join(",")]
  );

  const [entries, setEntries] = useState<MetricEntryValue[]>(initialEntries);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Sync entries if metrics change (new subKPIs added/removed)
  React.useEffect(() => {
    setEntries((prev) => {
      const existingByKey = new Map(prev.map((e) => [e.key, e]));
      return metrics.map((m) => {
        const existing = existingByKey.get(m.key);
        if (existing) return existing;
        return { key: m.key, value: "", note: "", dirty: false };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics.map((m) => m.key).join(",")]);

  const updateEntry = useCallback(
    (key: string, field: "value" | "note", val: string) => {
      setEntries((prev) =>
        prev.map((e) =>
          e.key === key ? { ...e, [field]: val, dirty: true } : e
        )
      );
      setStatus(null);
    },
    []
  );

  const dirtyEntries = useMemo(
    () => entries.filter((e) => e.dirty && e.value.trim() !== ""),
    [entries]
  );

  const handleSave = useCallback(async () => {
    if (dirtyEntries.length === 0) return;
    setSaving(true);
    setStatus(null);

    const payload: MetricEntrySavePayload[] = dirtyEntries
      .map((e) => ({
        key: e.key,
        value: parseFloat(e.value),
        note: e.note.trim(),
        reportingDate,
      }))
      .filter((p) => Number.isFinite(p.value));

    if (payload.length === 0) {
      setStatus({
        type: "error",
        message: t("dataEntry.invalidValues") || "Please enter valid numbers.",
      });
      setSaving(false);
      return;
    }

    try {
      await onSave(payload);
      // Clear dirty state and values on success
      setEntries((prev) =>
        prev.map((e) => ({ ...e, value: "", note: "", dirty: false }))
      );
      setStatus({
        type: "success",
        message:
          t("dataEntry.saveSuccess", { count: payload.length }) ||
          `${payload.length} value(s) saved.`,
      });
    } catch (err: any) {
      const detail = err?.message || "Unknown error";
      setStatus({
        type: "error",
        message: `${t("dataEntry.saveError") || "Save failed."} ${detail}`,
      });
    } finally {
      setSaving(false);
    }
  }, [dirtyEntries, reportingDate, onSave, t]);

  const handleCancel = useCallback(() => {
    setEntries(initialEntries);
    setStatus(null);
  }, [initialEntries]);

  if (metrics.length === 0) {
    return (
      <div className={styles.entryGridWrap}>
        <div className={styles.emptyState}>
          {t("dataEntry.noMetrics") ||
            "No metrics configured for this project."}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.entryGridWrap}>
      {/* Header */}
      <div className={styles.entryGridHeader}>
        <div className={styles.entryGridTitle}>
          {title || t("dataEntry.title") || "Enter Data"}
        </div>
        {!hideDateSelector && (
          <div className={styles.periodSelector}>
            <label className={styles.periodLabel} htmlFor="meg-date">
              {t("dataEntry.reportingDate") || "Reporting date"}:
            </label>
            <input
              id="meg-date"
              type="date"
              className={styles.dateInput}
              value={reportingDate}
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={readOnly}
            />
          </div>
        )}
      </div>

      {/* Table */}
      <table className={styles.entryTable}>
        <thead>
          <tr>
            <th>{t("dataEntry.colMetric") || "Metric"}</th>
            <th>{t("dataEntry.colCurrent") || "Current"}</th>
            <th>{t("dataEntry.colTarget") || "Target"}</th>
            <th>{t("dataEntry.colNewValue") || "New Value"}</th>
            <th>{t("dataEntry.colNote") || "Note"}</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m, idx) => {
            const entry = entries.find((e) => e.key === m.key);
            const vt = m.valueType ?? "numeric";
            return (
              <tr key={m.key}>
                <td>
                  <span className={styles.metricName}>
                    {entry?.dirty && (
                      <span className={styles.dirtyIndicator} />
                    )}
                    {m.label}
                  </span>
                  {m.unit && (
                    <span className={styles.metricUnit}>({m.unit})</span>
                  )}
                </td>
                <td className={styles.currentValue}>
                  {formatDisplay(m.currentValue, vt, m.milestoneTotal)}
                </td>
                <td className={styles.targetValue}>
                  {formatDisplay(m.targetValue, vt, m.milestoneTotal)}
                </td>
                <td>
                  {vt === "binary" ? (
                    <select
                      className={styles.valueInput}
                      value={entry?.value ?? ""}
                      onChange={(e) => updateEntry(m.key, "value", e.target.value)}
                      disabled={readOnly || saving}
                      tabIndex={idx * 2 + 1}
                      aria-label={`${m.label} value`}
                    >
                      <option value="">—</option>
                      <option value="1">{t("dataEntry.binaryYes") || "Yes"}</option>
                      <option value="0">{t("dataEntry.binaryNo") || "No"}</option>
                    </select>
                  ) : (
                    <input
                      type="number"
                      step={vt === "percentage" ? "0.1" : "any"}
                      className={styles.valueInput}
                      placeholder="—"
                      value={entry?.value ?? ""}
                      onChange={(e) => updateEntry(m.key, "value", e.target.value)}
                      disabled={readOnly || saving}
                      tabIndex={idx * 2 + 1}
                      aria-label={`${m.label} value`}
                    />
                  )}
                </td>
                <td>
                  <input
                    type="text"
                    className={styles.noteInput}
                    placeholder={t("dataEntry.notePlaceholder") || "Optional"}
                    value={entry?.note ?? ""}
                    onChange={(e) => updateEntry(m.key, "note", e.target.value)}
                    disabled={readOnly || saving}
                    tabIndex={idx * 2 + 2}
                    aria-label={`${m.label} note`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Footer */}
      {!readOnly && (
        <div className={styles.entryGridFooter}>
          <div>
            {status && (
              <span
                className={
                  status.type === "success"
                    ? styles.footerStatusSuccess
                    : status.type === "error"
                    ? styles.footerStatusError
                    : styles.footerStatus
                }
              >
                {status.message}
              </span>
            )}
            {!status && dirtyEntries.length > 0 && (
              <span className={styles.footerStatus}>
                {dirtyEntries.length}{" "}
                {t("dataEntry.pendingChanges") || "value(s) to save"}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className={styles.cancelBtn}
              onClick={handleCancel}
              disabled={saving || dirtyEntries.length === 0}
            >
              {t("dataEntry.cancel") || "Cancel"}
            </button>
            <button
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving || dirtyEntries.length === 0}
            >
              {saving
                ? t("dataEntry.saving") || "Saving…"
                : t("dataEntry.saveAll") || "Save All"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricEntryGrid;
